import {
  DEFAULT_ANOMALY_MIN_INTERVAL_MS,
  TelemetrySampler,
} from './telemetrySampler'
import { getAudioSentryPort, type AudioSentryPort } from './audioSentryPort'
import type { DeadAirEpisode, DeadAirFork, DeadAirSink } from './deadAirTypes'
import { isIPadOS } from './iPadOS'
import { isWebKit } from './webkit'

/** Sampler event name for the recovery-nudge gate. */
const DEAD_AIR_NUDGE_EVENT = 'meeting_dead_air_nudge'

/**
 * The cadence at which a *continuing* episode can reach `onEpisode`.
 *
 * The nudge gate is a per-listener min-interval, so a consumer that debounces
 * episodes into a dialog sees the same set of dead peers no more often than
 * this however continuously the audio is dead. Any window a consumer applies
 * must be measured against this cadence, not assumed to be instantaneous — a
 * window narrower than one interval is unreachable no matter how long the
 * client stays deaf.
 *
 * The one thing that gets through faster is a peer the window has not seen yet;
 * see {@link AudioEpisodeSink.emit}. That can only shorten the gap between two
 * episodes, never the age of the run a consumer measures from its first one.
 */
export const DEAD_AIR_EPISODE_MIN_INTERVAL_MS = DEFAULT_ANOMALY_MIN_INTERVAL_MS

/**
 * audioEpisodeSink — the one place a confirmed dead-air episode is written to
 * BOTH sinks.
 *
 * - **Sentry** holds the durable per-episode bundle (rich, per-peer): a
 *   `captureMessage('meeting_dead_air')` with queryable tags, the context ring
 *   as a stringified `audioRing` context, and the raw `stats` snapshot.
 *   Per-failure-type fingerprint keeps them from collapsing into one issue.
 *   Sentry is reached through an injected port (see audioSentryPort).
 * - **GA** holds the compact aggregate matrix: one `meeting_dead_air_scan` row
 *   of scalars only (GA silently truncates at 4000 rows/room and caps params,
 *   so it can't carry the per-episode bundle — that's why Sentry is the durable
 *   store).
 *
 * Both are anomaly-gated through the min-interval sampler (never 1-in-N): the
 * GA scan per `(listener)`, each Sentry peer message per
 * `(listener, peer, fork, transport verdict)`, so a single stuck peer can't
 * re-fire every cycle or drown out another peer. The last two belong in that
 * key because they are what the Sentry fingerprint groups on: a gate coarser
 * than the grouping empties whole issues, which is what kept `not_attached`
 * out of Sentry entirely.
 *
 * Browser, version and OS are not stamped here — GA4 and Sentry both derive
 * them from the User-Agent. Two platform facts are, because neither sink can
 * work them out: `iPadOS`, because an iPad reports a desktop-Macintosh UA and
 * both would otherwise file it as a Mac, and `webKit`, because the engine is
 * what the wiring checks branch on and the User-Agent hides it in both
 * directions — an iPad files as a Mac, and Chrome for iOS files as Chrome. A
 * row that cannot say which side of that branch it came from cannot be read
 * back against a verdict the engine decided.
 */

/**
 * The context ring, as this sink reads it: one call, at emit time.
 *
 * Typed as a one-method port rather than the concrete buffer so the ~30s
 * context-capture machinery is optional. A sink with no ring still writes the
 * full episode to both destinations; it just carries no lead-up.
 */
export interface EpisodeContextRing {
  serialize(): string
}

/**
 * Which repair the dialog offers. Not chosen from the failure type — every
 * failure gets the same two rungs — but from how far up the ladder this client
 * has already climbed: `reconnect` until that has been spent, then `reload`.
 * Offering a reconnect to someone who has already tried one is the dead end
 * this replaces.
 */
export type DeadAirNudgeMode = 'reconnect' | 'reload'

/**
 * What the ladder hands the dialog when it escalates.
 *
 * `cause` is one fork; `forks` is every distinct fork in the escalated set,
 * deduped and sorted. They differ whenever a single episode escalates peers
 * that failed differently — peer A `not_attached`, peer B `delivery_stalled` —
 * and the dialog's events used to report only the first, so the calibration
 * could not see what else was wrong with a client it had nudged. The set is
 * carried here rather than looked up from the episode because escalation is
 * per peer: an episode's dead set and its escalated set are not the same list
 * once auto-heal has taken the fixable half.
 */
export interface DeadAirNudgeRequest {
  /** The fork to attribute the nudge to: the first escalated peer's. */
  cause: DeadAirFork
  /** Every distinct fork among the escalated peers, sorted. Includes `cause`. */
  forks: DeadAirFork[]
  /** The peers this client was deaf to, for the post-repair recovery probe. */
  identities: string[]
  /**
   * The escalated peers as one comparable `identity:fork` string — see
   * {@link buildDeadAirCondition}.
   *
   * Not `condition`, which on every other row is the whole episode. This is a
   * subset — peers whose cheap fix is spent or was never available — so the two
   * cannot be pooled: `COUNT(DISTINCT condition)` over both reads one fault as
   * two. It also cannot simply carry the episode's string instead, because a
   * peer whose fix fails escalates from the verify pass seconds later, with no
   * episode of its own to take one from.
   *
   * Carried for the telemetry, never for the cooldown: a nudge that is
   * suppressed writes what the client's fault was at the time, while the
   * decision to suppress reads {@link DeadAirNudgeRequest.identities} alone.
   */
  escalatedCondition: string
  /** Which rung to offer. */
  mode: DeadAirNudgeMode
}

/**
 * What the verify re-check could conclude about one cheap fix.
 *
 * Three values rather than a healed flag because the probe has three answers.
 * `pending` is the one a boolean cannot carry: the detector refuses to judge a
 * peer whose track is inside its attach-grace window, on the grounds that an
 * element which has not rendered yet is no evidence either way. Folding that
 * into `still_dead` told the calibration a fix had failed when nothing had
 * been measured, and biased the numbers against exactly the fixes whose settle
 * window happens to overlap a re-subscription.
 *
 * It is its own value, rather than a flag beside `healed`, so a healed-rate
 * cannot be computed without deciding what to do with it. A `pending` row is
 * still worth writing: how often the verify lands inside grace is what says
 * whether `autoHealSettleMS` is set too short against `attachGraceMS`.
 */
export type HealVerdict = 'healed' | 'still_dead' | 'pending'

/** One cheap auto-heal attempt's result, for calibration telemetry. */
export interface HealOutcome {
  identity: string
  fork: DeadAirFork
  /** The fix tried ('play' | 'resink'). */
  fix: string
  /** What the wiring re-check found after the fix. */
  verdict: HealVerdict
  /** Time from applying the fix to the verify re-check. */
  verifyLatencyMs: number
  /**
   * Whether the auto-heal cycle was still the one this fix was applied in when
   * the verify ran.
   *
   * False means something else re-armed the ladder in between — most often
   * another peer's dialog-driven reconnect, which rebuilds every subscription
   * in the room. The wiring then reads clean for reasons that have nothing to
   * do with this fix, so a `healed` verdict from that verify is not evidence
   * about the fix. The outcome is still recorded, because a natural recovery
   * inside the settle window is worth counting; this flag is what lets the
   * calibration separate the two instead of crediting the fix for both.
   */
  sameCycle: boolean
}

/** Whether the room is mid-reconnect, so subscriptions are expected to churn. */
export function isReconnecting(connectionState: string): boolean {
  return (
    connectionState === 'reconnecting' ||
    connectionState === 'signalReconnecting'
  )
}

/** ICE states in which media can actually flow. */
const HEALTHY_ICE_STATES = new Set(['connected', 'completed'])
/** `RTCPeerConnectionState` has no `completed`; `connected` is its only healthy value. */
const HEALTHY_PC_STATES = new Set(['connected'])

/**
 * Whether the subscriber transport was down when this episode was built.
 *
 * An allowlist rather than a blocklist, in both directions. A state we failed
 * to enumerate is treated as unhealthy, because the failure that produces is a
 * suppressed scan — re-checked by the backstop 30s later — while the opposite
 * mistake is the false roster-wide report this whole gate exists to stop.
 *
 * An absent state is the one case that reads healthy. Both reads behind
 * {@link DeadAirEnvironment.subscriberTransport} go through livekit internals
 * or a non-universal WebRTC surface, so a future SDK layout change costs us the
 * field. When that happens the detector should get noisier, not quieter: a
 * blind spot that reports too much is visible in Sentry, and one that reports
 * nothing looks exactly like a fleet with no dead air.
 */
export function isTransportDown(episode: DeadAirEpisode): boolean {
  const { subscriberIceState: ice, subscriberPcState: pc } = episode
  if (ice !== undefined && !HEALTHY_ICE_STATES.has(ice)) return true
  if (pc !== undefined && !HEALTHY_PC_STATES.has(pc)) return true
  return false
}

/**
 * The failure type to record on an episode's nudge, or null when it isn't worth
 * one. The dialog does not branch on the value — it runs one repair and stamps
 * this on the heal events so the telemetry still says what was being repaired.
 *
 * A backgrounded tab pauses its <audio> elements (-> lost_play) and a
 * reconnecting room drops subscriptions (-> not_subscribed); both resolve on
 * their own, so nudging the user to repair them is wrong. A dead subscriber
 * transport is the third of these and the least visible: the room can read
 * `connected` right through it (see {@link DeadAirEpisode.subscriberIceState}),
 * so neither check above catches it, and every peer's element starves at once.
 * The cheap fix matched to the failure it produces is `play`, which cannot move
 * a starved element, so the ladder spends every peer's one attempt and hands
 * the whole roster to the dialog for a fault livekit is already repairing.
 * Otherwise take the first dead peer's failure type.
 */
export function resolveNudgeCause(episode: DeadAirEpisode): DeadAirFork | null {
  if (episode.pageHidden) return null
  if (isReconnecting(episode.connectionState)) return null
  if (isTransportDown(episode)) return null
  return episode.deadPeers[0]?.fork ?? null
}

/**
 * GA string values are capped at 100 chars.
 *
 * These fields are comma-joined lists, so a blind slice ends the value on a
 * half-identity (`peerA,peerB,peer`) that matches nothing and groups as its own
 * bucket in aggregate queries. Cutting back to the last separator inside the
 * limit keeps every entry that survives whole. A single entry longer than the
 * cap has no separator to fall back to and is sliced as before.
 */
export const truncate = (s: string, max = 100): string => {
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const lastComma = cut.lastIndexOf(',')
  return lastComma === -1 ? cut : cut.slice(0, lastComma)
}

/**
 * The episode's condition: every dead peer as `identity:fork`, sorted and
 * comma-joined (`peerA:lost_play,peerB:not_attached`).
 *
 * Sorted so the scan order the roster walk happened to condemn peers in cannot
 * make one condition read as two.
 *
 * A string rather than a hash, which is what the original proposal asked for.
 * The only operation performed on it is equality, and a string does equality
 * just as well while also saying what the condition was — where a responder
 * holding `a3f9c1` has to find a second row before the value means anything.
 * There is nothing to protect either: the same peer identities and forks are
 * already emitted raw in the `deadPeers` and `forks` columns of every scan row.
 *
 * What it answers: forty `meeting_dead_air` rows for one student are ambiguous
 * between one fault that never healed and forty separate faults, and those call
 * for opposite responses. `COUNT(DISTINCT condition)` settles it.
 */
export function buildDeadAirCondition(
  peers: readonly { identity: string; fork: DeadAirFork }[]
): string {
  return peers
    .map((p) => `${p.identity}:${p.fork}`)
    .sort()
    .join(',')
}

/**
 * 32-bit FNV-1a, as eight hex characters.
 *
 * Here to give {@link truncateCondition} something short and stable to append,
 * and used for nothing else. Not a security boundary and not trying to be: the
 * identities it digests are already emitted raw in the `deadPeers` column
 * beside it, so there is nothing here an attacker could not read off the same
 * row.
 */
function fnv1a(s: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i)
    // The 32-bit FNV prime, as shifts, because a plain multiply overflows the
    // float53 mantissa and stops being the same function on long inputs.
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
    hash >>>= 0
  }
  return hash.toString(16).padStart(8, '0')
}

/**
 * Cap a condition at the GA limit without costing it its identity as an
 * equality key.
 *
 * Plain {@link truncate} is right for `deadPeers` and `forks`, which are read
 * as "who and what, roughly", and wrong here. A condition entry is a 28-char
 * uid plus a fork, so it runs 38 to 47 characters: two entries always fit under
 * the cap and three never do. Every episode with three or more dead peers
 * therefore emits exactly its first two sorted entries. Because the sort is
 * lexicographic on identities that are stable for the life of a room, those two
 * are usually the same peers throughout an incident, so a third peer joining
 * the dead set — or peer C moving from `not_subscribed` to `lost_play` — leaves
 * the value byte-identical. `COUNT(DISTINCT condition)` then reads two faults
 * as one, which is the exact question the field exists to answer.
 *
 * The suffix carries what the cut threw away: how many peers went missing, and
 * a digest of the whole condition. Equality on the emitted value is equality on
 * the full one, while the head still names the two peers a responder would
 * chase first. A digest alone would have been cheaper and was rejected for the
 * reason on {@link buildDeadAirCondition}: `a3f9c1` means nothing until you
 * have found a second row carrying it.
 *
 * Raising the cap is not available. GA4 cuts a string parameter at 100
 * characters wherever it lands, and a value cut there arrives ending on a
 * half-identity.
 */
export function truncateCondition(condition: string, max = 100): string {
  if (condition.length <= max) return condition
  const digest = fnv1a(condition)
  const total = condition.split(',').length
  // Reserved against `total` rather than the count actually dropped, which is
  // not known until the head has been cut. Dropped is never larger than total,
  // so the reservation is never short and the result cannot overrun the cap.
  const head = truncate(condition, max - `+${total}~${digest}`.length)
  return `${head}+${total - head.split(',').length}~${digest}`
}

/**
 * Run an app-supplied side effect, swallowing anything it throws. Returns the
 * value, or undefined if it threw — see {@link AudioEpisodeSink.emit}.
 */
function guarded<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch {
    return undefined
  }
}

export interface AudioEpisodeSinkDeps {
  /** GA transport (repository.logEvent). */
  logEvent: (name: string, params: Record<string, unknown>) => void
  /** The min-interval anomaly sampler (shared with the detector). */
  sampler: TelemetrySampler
  /** The context ring; serialized into the Sentry bundle when one is wired. */
  ring?: EpisodeContextRing
  /** Lazy Sentry-port accessor (defaults to the registered global port). */
  sentry?: () => AudioSentryPort | null
  /** Injectable clock (defaults to Date.now). */
  now?: () => number
  /**
   * Called once per confirmed episode, after both sinks have written. The
   * controller uses this to drive the user-facing recovery nudge; the sink
   * stays free of any cause/heal policy.
   */
  onEpisode?: (episode: DeadAirEpisode) => void
}

export interface ReloadFlushOptions {
  /** Passed to Sentry.flush — how long to wait for the transport to drain. */
  flushTimeoutMs?: number
  /**
   * Only flush when an episode was emitted within this window before the page
   * left; an unrelated reload long after the last episode needs no flush.
   */
  pendingWindowMs?: number
}

const DEFAULT_FLUSH_TIMEOUT_MS = 2000
const DEFAULT_PENDING_WINDOW_MS = 8000

export class AudioEpisodeSink implements DeadAirSink {
  private readonly deps: AudioEpisodeSinkDeps
  private readonly sentry: () => AudioSentryPort | null
  private readonly now: () => number
  /** When the last durable episode was emitted (for the reload flush). */
  private lastEmitAt: number | null = null
  /**
   * The identities already handed to `onEpisode` inside the current nudge
   * window. Replaced whenever the sampler opens a new one.
   */
  private nudgedPeers = new Set<string>()
  /**
   * Per peer, what its last cheap fix achieved (`'<fix>:<verdict>'`), so the
   * next episode for that peer can say so on the report itself. Keyed by
   * identity and never cleared: a room's roster is small, and the value is
   * overwritten by the next attempt.
   */
  private readonly lastHeal = new Map<string, string>()
  private reloadFlushCleanup: (() => void) | null = null

  constructor(deps: AudioEpisodeSinkDeps) {
    this.deps = deps
    this.sentry = deps.sentry ?? getAudioSentryPort
    this.now = deps.now ?? Date.now
  }

  /**
   * Write a confirmed episode to both sinks. No-op when there are no dead
   * peers — GA only fires when peersDead > 0, and there is nothing durable to
   * capture otherwise.
   *
   * Every step is individually guarded. This runs inside a livekit
   * `trackSubscribed` listener and inside the slide transition, neither of
   * which catches: a throw from the GA transport, the Sentry port or the nudge
   * consumer would abort the remaining livekit listeners for that event — which
   * is how AudioObservabilitySources attaches its element listeners — or
   * propagate out of the slide change. Diagnostics must never break the thing
   * they are measuring, least of all at the moment they exist to observe.
   */
  emit(episode: DeadAirEpisode): void {
    const peersDead = episode.deadPeers.length
    if (peersDead === 0) return

    guarded(() => this.emitScanRow(episode, peersDead))
    guarded(() => this.emitSentryPeers(episode, peersDead))
    // Mark the moment for reload survival: the async Sentry transport may not
    // have drained if the page reloads in the next instant.
    this.lastEmitAt = this.now()
    // After both durable sinks: hand the episode to the recovery nudge. Gated
    // on its own key so a stuck peer can't re-offer the dialog on every
    // backstop sweep and slide change, and so the throttle it shares with
    // nothing else can't be spent by the GA or Sentry paths.
    const nudgeKey = TelemetrySampler.anomalyKey(
      DEAD_AIR_NUDGE_EVENT,
      episode.listenerId
    )
    // Deliberately a flat interval, not backoff: the UI decides which episodes
    // belong to the same run by measuring gaps against
    // DEAD_AIR_EPISODE_MIN_INTERVAL_MS, so stretching this gap would split one
    // continuous episode into runs that never reach the wait duration.
    //
    // The key is per-listener, but this hook drives the auto-heal ladder as
    // well as the dialog, and the ladder is per-peer. A flat listener gate
    // therefore swallowed the whole repair for anyone who broke second: peer A
    // breaks on a slide change and is fixed, peer B breaks five seconds later
    // on trackSubscribed and gets nothing until the interval elapses. So a peer
    // this window has not seen yet passes even when the gate is shut. Each
    // identity buys exactly one such pass per window — a set that flaps between
    // two peers can't ride the exception every cycle.
    const identities = episode.deadPeers.map((p) => p.identity)
    if (this.deps.sampler.allowAnomaly(nudgeKey)) {
      this.nudgedPeers = new Set(identities)
    } else if (identities.every((id) => this.nudgedPeers.has(id))) {
      return
    } else {
      for (const id of identities) this.nudgedPeers.add(id)
    }
    guarded(() => this.deps.onEpisode?.(episode))
  }

  /**
   * Reload survival: the durable episode is a client-side Sentry event, and the
   * dominant coping behavior in the corpus is an immediate page reload — which
   * can drop the event before its async transport leaves. Register a
   * `pagehide` / `visibilitychange → hidden` handler that flushes Sentry when a
   * fresh episode is still in flight. Firebase Analytics already flushes its
   * own queue on unload, so only the Sentry side needs an explicit flush here.
   *
   * Returns a cleanup fn; also stored so {@link removeReloadFlush} can undo it.
   *
   * Installing twice is a no-op that hands back the standing cleanup. Only one
   * cleanup can be tracked, so a second install would strand the first pair of
   * listeners: `removeReloadFlush` would unwind the newer pair and leave the
   * older one attached for the life of the page. The listeners are idempotent
   * on the flush itself, so the cost is a leak rather than a double-flush, but
   * a re-mount or an HMR pass is enough to hit it.
   */
  installReloadFlush(options: ReloadFlushOptions = {}): () => void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return () => {}
    }
    if (this.reloadFlushCleanup) return this.reloadFlushCleanup
    const flushTimeout = options.flushTimeoutMs ?? DEFAULT_FLUSH_TIMEOUT_MS
    const windowMs = options.pendingWindowMs ?? DEFAULT_PENDING_WINDOW_MS

    const flushIfPending = () => {
      if (this.lastEmitAt === null) return
      if (this.now() - this.lastEmitAt > windowMs) return
      // Guarded on both axes, the same rule the rest of the file follows: a
      // port that throws synchronously must not break `pagehide`, and a flush
      // that rejects must not surface as an unhandled rejection during unload,
      // which some browsers report as a page error. Nothing reads the result —
      // there is no recovery from a failed flush at this point in the page's
      // life, only the choice not to make it someone else's error.
      guarded(() => {
        void this.sentry()
          ?.flush(flushTimeout)
          .catch(() => {})
      })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushIfPending()
    }

    window.addEventListener('pagehide', flushIfPending)
    document.addEventListener('visibilitychange', onVisibility)
    const cleanup = () => {
      window.removeEventListener('pagehide', flushIfPending)
      document.removeEventListener('visibilitychange', onVisibility)
      this.reloadFlushCleanup = null
    }
    this.reloadFlushCleanup = cleanup
    return cleanup
  }

  removeReloadFlush(): void {
    this.reloadFlushCleanup?.()
  }

  /**
   * Record the outcome of one cheap auto-heal attempt: which failure was hit,
   * which fix was tried, whether it healed the peer, and how long the verify
   * took. This is how we tell a fix that works from one that quietly makes
   * things worse before trusting auto-heal — so it is emitted whether or not the
   * fix worked. Guarded like emit(): diagnostics must never break the room.
   *
   * Three destinations, and the Sentry message is the one that has to exist.
   * The breadcrumb cannot answer "did a repair run" for the episode that
   * triggered it: reporting happens before the ladder, so the outcome is
   * recorded after the `captureMessage` it belongs to, and Sentry attaches only
   * breadcrumbs older than the event. A peer the fix repaired never produces
   * another event, so on the breadcrumb path a *successful* heal is invisible
   * by construction. Its own message is not.
   *
   * Ungated by the sampler, unlike the episode messages. A peer's cheap fix is
   * spent once per cycle and a cycle re-arms only on a genuine recovery, so the
   * volume is bounded by how often the client actually recovers — and a
   * throttle here would drop exactly the confirmations this exists to collect.
   */
  emitHealOutcome(outcome: HealOutcome): void {
    this.lastHeal.set(outcome.identity, `${outcome.fix}:${outcome.verdict}`)
    guarded(() =>
      this.sentry()?.captureMessage({
        message: 'meeting_dead_air_heal',
        level: outcome.verdict === 'still_dead' ? 'warning' : 'info',
        tags: {
          peer: outcome.identity,
          fork: outcome.fork,
          fix: outcome.fix,
          verdict: outcome.verdict,
          sameCycle: outcome.sameCycle,
          iPadOS: isIPadOS(),
          webKit: isWebKit(),
        },
        contexts: { heal: { verifyLatencyMs: outcome.verifyLatencyMs } },
        // Grouped by what was tried and what it achieved, so "does resink ever
        // work" is one issue to read rather than a search across every peer.
        fingerprint: ['meeting_dead_air_heal', outcome.fix, outcome.verdict],
      })
    )
    guarded(() =>
      this.deps.logEvent('meeting_dead_air_heal', {
        fork: outcome.fork,
        fix: outcome.fix,
        verdict: outcome.verdict,
        verifyLatencyMs: outcome.verifyLatencyMs,
        sameCycle: outcome.sameCycle,
        iPadOS: isIPadOS(),
        webKit: isWebKit(),
      })
    )
    guarded(() =>
      this.sentry()?.addBreadcrumb({
        category: 'audio',
        message: 'meeting_dead_air_heal',
        // Only a fix that was measured and failed is a warning. `pending` is
        // an unmeasured attempt, and filing it at the same level as a failure
        // is what the third verdict exists to stop.
        level: outcome.verdict === 'still_dead' ? 'warning' : 'info',
        data: {
          identity: outcome.identity,
          fork: outcome.fork,
          fix: outcome.fix,
          verdict: outcome.verdict,
          verifyLatencyMs: outcome.verifyLatencyMs,
          sameCycle: outcome.sameCycle,
        },
      })
    )
  }

  /**
   * GA aggregate matrix row — scalars only, one per scan.
   *
   * Watch the parameter count. GA4 caps an event at 25 parameters and silently
   * drops the overflow, and this row is not the whole budget: `logGaEvent` adds
   * five identifiers (room, assignment, section, slide deck, active slide)
   * before it is sent. As of this comment the row is 18 at its widest — every
   * field below, plus `fork` on a single-peer scan and `detectorVersion` —
   * which leaves two spare. A field added here without dropping one is likely
   * to be the one that goes missing, and nothing reports it.
   */
  private emitScanRow(episode: DeadAirEpisode, peersDead: number): void {
    const scanKey = TelemetrySampler.anomalyKey(
      'meeting_dead_air_scan',
      episode.listenerId
    )
    if (!this.deps.sampler.allowAnomaly(scanKey)) return

    const params: Record<string, unknown> = {
      peersDead,
      peersSubscribed: episode.peersSubscribed,
      peersPublishing: episode.peersPublishing,
      pageHidden: episode.pageHidden,
      // Every half of the nudge-cause gate, so a row says whether the recovery
      // ladder ran. `resolveNudgeCause` returns null for a hidden page, a
      // reconnecting room or a dead subscriber transport and `onEpisode`
      // returns on that, which means no repair is attempted and no dialog is
      // offered — and without these the row is identical to one whose fix ran
      // and healed the peer silently.
      connectionState: episode.connectionState,
      // Not derivable from connectionState: the room reads `connected` through
      // a dead transport, which is the case this field exists to name. Any
      // rate computed over these rows that does not split on it is counting
      // transport outages as local playback faults.
      transportDown: isTransportDown(episode),
      subscriberIceState: episode.subscriberIceState ?? '',
      secondsSinceSlideChange: episode.secondsSinceSlideChange ?? -1,
      triggeringSpeaker: episode.triggeringSpeaker ?? '',
      // Deliberately raw, in roster order, and deliberately not the same shape
      // as the Sentry `forks` tag, which is deduped and sorted. These two are
      // positionally paired: index i of `forks` is the failure type of index i
      // of `deadPeers`, which is the only way a GA row of scalars can say which
      // peer hit which fault. Deduping would destroy that pairing on every row.
      // The Sentry side is a queryable tag rather than a list, so there the
      // stable deduped form is the useful one. The pairing degrades if either
      // value overruns the 100-char cap and truncates to a different count —
      // acceptable, because peersDead is 1-3 in the cases these rows answer.
      deadPeers: truncate(episode.deadPeers.map((p) => p.identity).join(',')),
      forks: truncate(episode.deadPeers.map((p) => p.fork).join(',')),
      // How long this client has been deaf in the current run. -1 rather than
      // an absent param when the detector did not stamp one, so a row that
      // predates the field is not read as a run that had just started.
      deadAirDurationMs: episode.deadAirDurationMs ?? -1,
      // Not truncated the way `deadPeers` and `forks` are. Those are read as
      // "who and what, roughly", and losing a tail entry costs them nothing.
      // This one is an equality key, so it keeps a digest of what the cut
      // dropped. See `truncateCondition`.
      condition: truncateCondition(buildDeadAirCondition(episode.deadPeers)),
      // The envelope the fault sits in. Empty and -1 rather than absent, so a
      // row where the read failed is visible as such instead of silently
      // dropping out of a GROUP BY.
      livekitConnectionQuality: episode.livekitConnectionQuality ?? '',
      roomParticipantCount: episode.roomParticipantCount ?? -1,
      iPadOS: isIPadOS(),
      webKit: isWebKit(),
    }
    // Pairwise convenience: single-peer scans carry the failure type directly.
    if (peersDead === 1) params.fork = episode.deadPeers[0].fork
    // Written only when the detector stamped one, so an absent value keeps
    // meaning "emitted before this field existed" rather than being pooled with
    // version 1.
    if (episode.detectorVersion !== undefined) {
      params.detectorVersion = episode.detectorVersion
    }
    this.deps.logEvent('meeting_dead_air_scan', params)
  }

  /** Durable Sentry bundle — one captureMessage per dead peer. */
  private emitSentryPeers(episode: DeadAirEpisode, peersDead: number): void {
    const port = this.sentry()
    if (!port) return

    const audioRing = this.deps.ring?.serialize()
    // The whole episode's failure-type set, carried on every per-peer message.
    // The per-peer throttle can drop a sibling peer's message (same pair
    // re-firing, backoff), so a reader of any single delivered report would
    // otherwise see only `peersDead: N` and this peer's own failure — a count,
    // not the second diagnosis. This list makes one report enough to see the
    // client was hitting more than one failure at once. Distinct + sorted so
    // it's a stable tag.
    const forks = [...new Set(episode.deadPeers.map((p) => p.fork))]
      .sort()
      .join(',')
    const transportDown = isTransportDown(episode)
    // Capped at the GA limit rather than Sentry's looser 200, so a responder
    // comparing this tag against a BigQuery row is comparing the same string.
    const condition = truncateCondition(
      buildDeadAirCondition(episode.deadPeers)
    )
    const deadPeersDetail = episode.deadPeers.map((p) => ({
      identity: p.identity,
      fork: p.fork,
    }))
    for (const peer of episode.deadPeers) {
      // Keyed on the failure type as well as the pair, so the throttle matches
      // the fingerprint below: grouping is per fork, and a gate coarser than
      // the grouping silently empties a whole issue. A peer moves between forks
      // in the ordinary course — `classifyPublication` can only reach
      // `not_attached` through a track that was `not_subscribed` a moment
      // earlier — so a pair-only key meant the *second* diagnosis of a peer was
      // dropped as a duplicate of the first. Production ran sixteen days with
      // four users hitting `not_attached` and not one report of it reaching
      // Sentry.
      //
      // The transport verdict is the fingerprint's third dimension and so
      // belongs in the key for the same reason. Without it a peer already
      // streaking on `lost_play` with the transport up is gated when the
      // transport dies, and the gate backs off toward
      // `DEFAULT_MAX_ANOMALY_INTERVAL_MS` — eight minutes — so an outage
      // shorter than that produces no `transport_down` report for that peer.
      //
      // The bound this gives up is small: one live gate per (pair, fork,
      // verdict), and `DeadAirFork` has eight members, so a peer flapping
      // across every fork costs eight reports rather than one before backoff
      // catches up.
      const key = TelemetrySampler.anomalyKey(
        'meeting_dead_air',
        episode.listenerId,
        `${peer.identity}:${peer.fork}:${transportDown}`
      )
      // Backoff: this key fans out per dead peer and stays hot for as long as
      // the client is deaf, so a flat interval bills the most events during a
      // fleet-wide outage. The first few describe the fault; the rest are
      // copies. Reporting never stops — a silent client would read as
      // recovered — it just slows to the ceiling.
      if (!this.deps.sampler.allowAnomaly(key, { backoff: true })) continue

      const tags: Record<string, string | number | boolean> = {
        iPadOS: isIPadOS(),
        webKit: isWebKit(),
        // Who this report is about. The identity is otherwise only inside the
        // `deadPeers` roster context, which names every dead peer and so can't
        // say which one this message diagnoses — and contexts aren't queryable,
        // so "every report against this peer" needs a tag. Tagged and not
        // fingerprinted on purpose: grouping stays per failure type, or a
        // fleet-wide fault would open one issue per participant.
        peer: peer.identity,
        fork: peer.fork,
        // Every distinct failure type in this episode (this peer's included),
        // so the tag is queryable for "more than one failure at once" even from
        // a single delivered message.
        forks,
        peersDead,
        pageHidden: episode.pageHidden,
        // What the last cheap fix for this peer achieved, or 'none'. The heal
        // that precedes an episode is the one thing this report cannot carry
        // any other way — its breadcrumb is younger than the event — so an
        // escalation says whether it was reached with a repair already tried
        // and failed, or with no repair attempted at all.
        lastHeal: this.lastHeal.get(peer.identity) ?? 'none',
        // Tagged beside pageHidden because these are read together: they are
        // the set that decides whether this episode reached the recovery ladder
        // at all. An episode that did not looks exactly like one whose cheap
        // fix ran, because the heal is recorded after this message and so can
        // never appear in its own breadcrumbs.
        connectionState: episode.connectionState,
        // The third member of that set, and the only one the room state cannot
        // stand in for. Raw states as well as the verdict: the verdict is what
        // groups, but a reader chasing an SDK change needs to see which of the
        // two reads produced it, and `''` distinguishes "read it, no opinion"
        // from a tag that was never written.
        transportDown,
        subscriberIceState: episode.subscriberIceState ?? '',
        subscriberPcState: episode.subscriberPcState ?? '',
        // Both run clocks: this peer's, and the whole client's. They differ
        // once a second peer joins a run already in progress, and a report
        // carrying only one of them cannot say which case it is looking at.
        deadForMs: peer.deadForMs ?? -1,
        deadAirDurationMs: episode.deadAirDurationMs ?? -1,
        // What was wrong with the whole client, as one comparable value. Two
        // reports carrying the same condition describe one standing fault; two
        // carrying different ones describe two faults.
        condition,
        // The envelope: whose network, and how big a room.
        livekitConnectionQuality: episode.livekitConnectionQuality ?? '',
        roomParticipantCount: episode.roomParticipantCount ?? -1,
      }

      const contexts: Record<string, Record<string, unknown>> = {
        // Two contexts, not one blob: the wiring picture answers "was this
        // client ever set up to hear the peer", the receiver stats answer
        // "did the audio arrive and decode". A report that mixes them makes
        // the reader sort thirty keys to find out which question failed.
        wiring: { snapshot: peer.wiring ?? null },
        // `ageMs` is not decoration: a structural fault carries the last sample
        // the flow checks read rather than one of its own, so a reader who
        // takes these counters for a live read would date the silence to the
        // wrong moment. 0 means read to reach this verdict; null means no
        // sample travelled with the episode.
        receiverStats: {
          snapshot: peer.receiverStats ?? null,
          ageMs: peer.receiverStats ? (peer.receiverStatsAgeMs ?? null) : null,
        },
        // The full dead-peer roster (identity + failure type), so one report
        // names the other concurrent issues rather than just counting them.
        deadPeers: { list: deadPeersDetail },
      }
      // Absent, not defaulted, for the same reason it is on the GA row.
      if (episode.detectorVersion !== undefined) {
        tags.detectorVersion = episode.detectorVersion
      }

      // Stringified so Sentry's normalizeDepth/maxValueLength can't mangle it.
      if (audioRing !== undefined) contexts.audioRing = { entries: audioRing }

      // Guarded per peer, not once around the loop: a whole-client episode is
      // the case where the sibling reports matter most, and a single throwing
      // message would otherwise take every peer after it with it. Peers that
      // never reach `allowAnomaly` keep their sampler slot and can report on
      // the next scan, so the loss is confined to a transient episode — which
      // is exactly the kind that never comes back.
      guarded(() =>
        port.captureMessage({
          message: 'meeting_dead_air',
          level: 'warning',
          tags,
          contexts,
          // Per-failure-type fingerprint so pairwise/global ones stay separate
          // issues, split again on whether the subscriber transport was up.
          //
          // Without the second dimension both populations land in one issue,
          // and the transport-down one is not a minority: a dead transport
          // starves every attached element at once, so it contributes a whole
          // roster of `lost_play` per episode where a genuine playback fault
          // usually contributes one peer. Pooled, they make `lost_play` look
          // like the dominant local fault and put a floor under its rate that
          // no amount of playback fixing can move.
          fingerprint: [
            'meeting_dead_air',
            peer.fork,
            transportDown ? 'transport_down' : 'transport_up',
          ],
        })
      )
    }
  }
}
